import React, { useState, useEffect, useRef, useCallback } from 'react';
import { SearchIcon, LocationIcon } from './Icons';
import { apiService } from '../auth';

// Search Component avec autocomplétion depuis les données locales
export const SearchBar = ({ placeholder = "Rechercher dans LaTeranga...", onSearch, enableAutocomplete = false, existingListings = [] }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const searchRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Sélectionner une suggestion
  const selectSuggestion = useCallback((suggestion) => {
    setSearchTerm(suggestion.title);
    setShowSuggestions(false);
    setActiveSuggestion(-1);
    if (onSearch) {
      onSearch(suggestion.title);
    }
  }, [onSearch]);

  // Recherche normale
  const handleSearch = useCallback(() => {
    if (onSearch && searchTerm.trim()) {
      onSearch(searchTerm);
      setShowSuggestions(false);
    }
  }, [onSearch, searchTerm]);

  // Générer les suggestions depuis les annonces existantes
  const generateSuggestions = useCallback((query) => {
    if (query.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsLoading(true);
    
    try {
      const queryLower = query.toLowerCase();
      
      // Fonction pour calculer la pertinence d'une annonce
      const calculateRelevance = (listing) => {
        let score = 0;
        const title = listing.title?.toLowerCase() || '';
        const description = listing.description?.toLowerCase() || '';
        const location = listing.location?.toLowerCase() || '';
        
        // Score basé sur la correspondance dans le titre (plus important)
        if (title.includes(queryLower)) {
          score += title.startsWith(queryLower) ? 100 : 50;
        }
        
        // Score basé sur la correspondance dans la description
        if (description.includes(queryLower)) {
          score += 20;
        }
        
        // Score basé sur la localisation
        if (location.includes(queryLower)) {
          score += 10;
        }
        
        // Bonus pour les mots complets
        const words = queryLower.split(' ');
        words.forEach(word => {
          if (word.length > 2) {
            if (title.includes(word)) score += 15;
            if (description.includes(word)) score += 5;
          }
        });
        
        return score;
      };
      
      // Filtrer et trier les annonces par pertinence
      const relevantListings = existingListings
        .map(listing => ({
          ...listing,
          relevance: calculateRelevance(listing)
        }))
        .filter(listing => listing.relevance > 0)
        .sort((a, b) => b.relevance - a.relevance)
        .slice(0, 5); // Limiter à 5 suggestions
      
      // Formatter les suggestions
      const suggestionsList = relevantListings.map(listing => {
        // Récupérer l'URL de l'image
        let imageUrl = null;
        if (listing.images && listing.images.length > 0) {
          const firstImage = listing.images[0];
          if (typeof firstImage === 'object') {
            imageUrl = firstImage.secure_url || firstImage.url || firstImage.original;
          } else if (typeof firstImage === 'string') {
            imageUrl = firstImage;
          }
        }
        
        return {
          id: listing.id,
          title: listing.title,
          price: listing.price,
          location: listing.location,
          image: imageUrl,
          relevance: listing.relevance
        };
      });
      
      setSuggestions(suggestionsList);
      setShowSuggestions(suggestionsList.length > 0);
      
    } catch (error) {
      console.error('Erreur lors de la génération des suggestions:', error);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsLoading(false);
    }
  }, [existingListings]);

  // Gérer les touches du clavier
  const handleKeyDown = useCallback((e) => {
    if (!showSuggestions) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveSuggestion(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveSuggestion(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (activeSuggestion >= 0) {
          selectSuggestion(suggestions[activeSuggestion]);
        } else {
          handleSearch();
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setActiveSuggestion(-1);
        break;
    }
  }, [showSuggestions, suggestions, activeSuggestion, selectSuggestion, handleSearch]);

  // Gérer les changements dans l'input
  const handleInputChange = useCallback((e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setActiveSuggestion(-1);
    
    // Générer directement les suggestions si nécessaire
    if (enableAutocomplete && value.trim() && value.length >= 2) {
      generateSuggestions(value);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [enableAutocomplete, generateSuggestions]);

  // Fermer les suggestions quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target) &&
          suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setShowSuggestions(false);
        setActiveSuggestion(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={searchRef}>
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <SearchIcon />
      </div>
      <input
        type="text"
        className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        placeholder={placeholder}
        value={searchTerm}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onKeyPress={(e) => e.key === 'Enter' && !showSuggestions && handleSearch()}
        onFocus={() => {
          if (suggestions.length > 0) {
            setShowSuggestions(true);
          }
        }}
      />
      
      {/* Loading indicator */}
      {isLoading && (
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
          <svg className="w-4 h-4 animate-spin text-gray-400" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      )}

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div 
          ref={suggestionsRef}
          className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg z-50 mt-1 max-h-80 overflow-y-auto"
        >
          {suggestions.map((suggestion, index) => (
            <div
              key={suggestion.id}
              className={`p-3 cursor-pointer border-b border-gray-100 last:border-b-0 flex items-center gap-3 hover:bg-gray-50 ${
                index === activeSuggestion ? 'bg-blue-50' : ''
              }`}
              onClick={() => selectSuggestion(suggestion)}
            >
              {suggestion.image && (
                <img 
                  src={suggestion.image} 
                  alt={suggestion.title}
                  className="w-10 h-10 rounded object-cover"
                />
              )}
              <div className="flex-1">
                <div className="font-medium text-gray-900 text-sm">
                  {suggestion.title}
                </div>
                <div className="flex justify-between items-center mt-1">
                  <span className="text-blue-600 font-semibold text-sm">
                    €{typeof suggestion.price === 'number' ? suggestion.price.toFixed(2) : suggestion.price}
                  </span>
                  <span className="text-gray-500 text-xs">
                    {suggestion.location}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Location Picker Component
export const LocationPicker = () => (
  <div className="flex items-center gap-2 bg-gray-100 px-3 py-2 rounded-lg">
    <LocationIcon />
    <span className="text-sm text-gray-600">Paris, France</span>
  </div>
);
